import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { CheckCircle, Loader2, Paperclip, RefreshCw, Upload } from "lucide-react";
import { CommissionRequest } from "@/types/commission.types";
import {
  useCommissionDeliveries,
  useCreateCommissionDelivery,
  useUpdateCommissionDelivery,
  useUploadDeliveryImage
} from "@/hooks/commission";

interface CommissionDeliveryManagerProps {
  request: CommissionRequest;
}

export function CommissionDeliveryManager({ request }: CommissionDeliveryManagerProps) {
  const { data: deliveries = [], isLoading: isLoadingDeliveries } = useCommissionDeliveries(request.id);
  const { mutate: createDelivery, isPending: isCreatingDelivery } = useCreateCommissionDelivery();
  const { mutate: updateDelivery, isPending: isUpdatingDelivery } = useUpdateCommissionDelivery();
  const { uploadImage, isUploading, progress } = useUploadDeliveryImage();
  const [deliveryUrl, setDeliveryUrl] = useState('');
  const [isFinal, setIsFinal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [revisionRequested, setRevisionRequested] = useState(false);
  
  const handleDeliverySubmit = async () => {
    if (!deliveryUrl) {
      toast.error("Por favor, adicione a URL da entrega.");
      return;
    }
    
    createDelivery({
      commission_request_id: request.id,
      delivery_url: deliveryUrl,
      is_final: isFinal,
      revision_requested: revisionRequested
    });
  };
  
  const handleFileUpload = async (file: File) => {
    try {
      const url = await uploadImage(file);
      setDeliveryUrl(url);
    } catch (error) {
      toast.error("Erro ao enviar a imagem.");
    }
  };
  
  const handleRevisionRequest = (deliveryId: string) => {
    updateDelivery({
      id: deliveryId,
      delivery: { revision_requested: true }
    });
  };
  
  const handleApproveDelivery = (deliveryId: string) => {
    updateDelivery({
      id: deliveryId,
      delivery: { is_final: true }
    });
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Entregas da Comissão</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoadingDeliveries ? (
          <p>Carregando entregas...</p>
        ) : (
          <div className="space-y-4">
            {deliveries.map((delivery) => (
              <div key={delivery.id} className="border rounded-md p-4">
                <div className="mb-2">
                  <a href={delivery.delivery_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                    Visualizar Entrega
                  </a>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Entregue em: {new Date(delivery.created_at).toLocaleDateString()}
                  </p>
                  
                  {delivery.is_final ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : delivery.revision_requested ? (
                    <RefreshCw className="h-5 w-5 text-orange-500" />
                  ) : null}
                </div>
                
                <Separator className="my-3" />
                
                <div className="flex justify-end gap-2">
                  {!delivery.is_final && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRevisionRequest(delivery.id)}
                        disabled={isUpdatingDelivery}
                      >
                        Solicitar Revisão
                      </Button>
                      
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => handleApproveDelivery(delivery.id)}
                        disabled={isUpdatingDelivery}
                      >
                        Aprovar Entrega
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            
            <Separator />
            
            <div>
              <h3 className="text-lg font-medium mb-2">Nova Entrega</h3>
              
              <div className="mb-4">
                <Input
                  type="file"
                  disabled={isUploading}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleFileUpload(e.target.files[0]);
                    }
                  }}
                />
                {isUploading && (
                  <progress value={progress} max="100">
                    {progress}%
                  </progress>
                )}
                {deliveryUrl && (
                  <a href={deliveryUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                    Visualizar Arquivo
                  </a>
                )}
              </div>
              
              <div className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  id="isFinal"
                  className="h-4 w-4"
                  checked={isFinal}
                  onChange={(e) => setIsFinal(e.target.checked)}
                />
                <label htmlFor="isFinal" className="text-sm text-gray-700">Entrega Final?</label>
              </div>
              
              <Textarea
                placeholder="Feedback (opcional)"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="resize-none"
              />
              
              <Button onClick={handleDeliverySubmit} disabled={isCreatingDelivery || isUploading}>
                {isCreatingDelivery ? (
                  <>
                    Enviando...
                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  </>
                ) : (
                  "Enviar Entrega"
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
